const TARGET = 0.944; // -0.5 dBFS — headroom for inter-sample peaks

function peakGain(buffer: AudioBuffer): number {
  let peak = 0;
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < buffer.length; i++) {
      const abs = Math.abs(data[i] ?? 0);
      if (abs > peak) peak = abs;
    }
  }
  return peak > 0.001 ? TARGET / peak : 1.0;
}

function encodeWav(buffer: AudioBuffer, gain: number): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numSamples = buffer.length;
  const bytesPerSample = 2; // 16-bit PCM
  const dataSize = numSamples * numChannels * bytesPerSample;
  const ab = new ArrayBuffer(44 + dataSize);
  const view = new DataView(ab);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channels.push(buffer.getChannelData(ch));
  }

  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    for (const ch of channels) {
      const s = Math.max(-1, Math.min(1, (ch[i] ?? 0) * gain));
      view.setInt16(offset, Math.round(s < 0 ? s * 0x8000 : s * 0x7fff), true);
      offset += 2;
    }
  }

  return new Blob([ab], { type: "audio/wav" });
}

// Normalizes the full file to -0.5 dBFS for consistent preview playback.
export async function normalizeAudioFile(file: File): Promise<File> {
  const arrayBuffer = await file.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(arrayBuffer);
    const wavBlob = encodeWav(decoded, peakGain(decoded));
    const baseName = file.name.replace(/\.[^.]+$/, "");
    return new File([wavBlob], `${baseName}.wav`, { type: "audio/wav" });
  } finally {
    await ctx.close();
  }
}

export async function trimAudioFile(file: File, startPct: number, endPct: number): Promise<File> {
  const arrayBuffer = await file.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(arrayBuffer);
    const { sampleRate, numberOfChannels, length } = decoded;

    // Gain is based on the full file so the trimmed segment's volume
    // is consistent regardless of which part was selected.
    const gain = peakGain(decoded);

    const startSample = Math.floor(startPct * length);
    const endSample = Math.min(Math.ceil(endPct * length), length);

    const trimmed = ctx.createBuffer(numberOfChannels, endSample - startSample, sampleRate);
    for (let ch = 0; ch < numberOfChannels; ch++) {
      trimmed.getChannelData(ch).set(decoded.getChannelData(ch).subarray(startSample, endSample));
    }

    const wavBlob = encodeWav(trimmed, gain);
    const baseName = file.name.replace(/\.[^.]+$/, "");
    return new File([wavBlob], `${baseName}-trimmed.wav`, { type: "audio/wav" });
  } finally {
    await ctx.close();
  }
}
